import {
  LayoutDashboard,
  CalendarDays,
  MessageSquare,
  Receipt,
  Users,
  BarChart3,
  Megaphone,
  Settings,
  Plug,
} from 'lucide-react';

export const WORKSPACE_ROOT = '/app';
export const ONBOARDING_PATH = `${WORKSPACE_ROOT}/onboarding`;

// Front Desk nested sections — single source of truth for routes and tab labels.
export const frontDeskSubtabs = [
  {
    id: 'today',
    path: 'today',
    label: 'Today\u2019s appointments',
    shortLabel: 'Today',
  },
  {
    id: 'queue',
    path: 'queue',
    label: 'Walk-ins & queue',
    shortLabel: 'Queue',
  },
  {
    id: 'checkout',
    path: 'checkout',
    label: 'Checkout',
    shortLabel: 'Checkout',
  },
  {
    id: 'till',
    path: 'till',
    label: 'Till & cash up',
    shortLabel: 'Till',
  },
  {
    id: 'close',
    path: 'close',
    label: 'Day close',
    shortLabel: 'Close',
  },
];

export const messagesSubtabs = [
  {
    id: 'whatsapp',
    path: 'whatsapp',
    label: 'WhatsApp Business',
    shortLabel: 'WhatsApp',
  },
  { id: 'templates', path: 'templates', label: 'Templates', shortLabel: 'Templates' },
  {
    id: 'reminders',
    path: 'reminders',
    label: 'Automated reminders',
    shortLabel: 'Reminders',
  },
];

export const salesSubtabs = [
  {
    id: 'transactions',
    path: 'transactions',
    label: 'Transactions',
    shortLabel: 'Transactions',
  },
  { id: 'deposits', path: 'deposits', label: 'Deposits', shortLabel: 'Deposits' },
];

export const reportsSubtabs = [
  { id: 'overview', path: 'overview', label: 'Overview', shortLabel: 'Overview' },
  {
    id: 'sales-summary',
    path: 'sales-summary',
    label: 'Sales summary',
    shortLabel: 'Sales',
  },
  {
    id: 'staff',
    path: 'staff',
    label: 'Staff performance',
    shortLabel: 'Staff',
  },
  {
    id: 'retention',
    path: 'retention',
    label: 'Client retention',
    shortLabel: 'Retention',
  },
  {
    id: 'reconciliation',
    path: 'reconciliation',
    label: 'Payment reconciliation',
    shortLabel: 'Reconcile',
  },
];

// Primary navigation for the signed-in business workspace. Each tab owns a route and
// will grow the `subtabs` listed here into real nested routes.
export const primaryNav = [
  {
    id: 'front-desk',
    label: 'Front Desk',
    path: `${WORKSPACE_ROOT}/front-desk`,
    icon: LayoutDashboard,
    tagline: 'Run the floor today',
    description:
      'The single screen your team keeps open all day: who is arriving next, who is waiting, and what has been paid.',
    subtabs: frontDeskSubtabs.map((tab) => tab.label),
  },
  {
    id: 'calendar',
    label: 'Calendar',
    path: `${WORKSPACE_ROOT}/calendar`,
    icon: CalendarDays,
    tagline: 'Every chair, room and therapist',
    description:
      'Staff, rooms and chairs in one view, with real service durations and buffer time so the day actually fits.',
    subtabs: [
      'Day view',
      'Week view',
      'Staff shifts',
      'Time off & closures',
      'Waitlist',
    ],
  },
  {
    id: 'messages',
    label: 'Messages',
    path: `${WORKSPACE_ROOT}/messages`,
    icon: MessageSquare,
    tagline: 'WhatsApp for your salon',
    description:
      'Connect WhatsApp Business, save templates, and run automated reminders.',
    subtabs: messagesSubtabs.map((tab) => tab.label),
  },
  {
    id: 'sales',
    label: 'Sales',
    path: `${WORKSPACE_ROOT}/sales`,
    icon: Receipt,
    tagline: 'Mobile money, card and cash',
    description:
      'Deposits taken online and payouts to the wallet you connected.',
    subtabs: salesSubtabs.map((tab) => tab.label),
  },
  {
    id: 'clients',
    label: 'Clients',
    path: `${WORKSPACE_ROOT}/clients`,
    icon: Users,
    tagline: 'The client book that follows every visit',
    description:
      'Contact details, visit history, formulas and preferences — so any stylist can pick up where the last one left off.',
    subtabs: [
      'Client list',
      'Profiles & history',
      'Notes & preferences',
      'Reviews',
      'Import contacts',
    ],
  },
  {
    id: 'reports',
    label: 'Reports',
    path: `${WORKSPACE_ROOT}/reports`,
    icon: BarChart3,
    tagline: 'See what is working before month end',
    description:
      'Revenue, occupancy, repeat visits and staff performance, reconciled against what actually landed in your account.',
    subtabs: reportsSubtabs.map((tab) => tab.label),
  },
  {
    id: 'marketing',
    label: 'Marketing',
    path: `${WORKSPACE_ROOT}/marketing`,
    icon: Megaphone,
    tagline: 'Fill the quiet hours',
    description:
      'Your booking link, promotions and loyalty rewards — the tools that bring clients back without chasing them.',
    subtabs: [
      'Campaigns',
      'Promotions & discounts',
      'Loyalty',
      'Booking link & profile',
      'Social sharing',
    ],
  },
  {
    id: 'integrations',
    label: 'Integrations',
    path: `${WORKSPACE_ROOT}/integrations`,
    icon: Plug,
    tagline: 'Rails that work here',
    description:
      'WhatsApp and other connectors for your salon.',
    subtabs: ['WhatsApp Business'],
  },
  {
    id: 'manage',
    label: 'Manage',
    path: `${WORKSPACE_ROOT}/manage`,
    icon: Settings,
    tagline: 'Set the business up once',
    description:
      'Services and pricing, your team and their permissions, locations, online booking rules and payouts.',
    subtabs: [
      'Business profile',
      'Booking link',
      'Payments',
      'Services & pricing',
      'Team & permissions',
      'Locations & resources',
    ],
  },
];

export const DEFAULT_WORKSPACE_PATH = primaryNav[0].path;

export const getNavItem = (id) => primaryNav.find((item) => item.id === id);
